/* eslint-disable @typescript-eslint/naming-convention */
import i18next from "i18next";
import * as DOMPurify from "dompurify";

let localeData: LocaleData = {};
let currentLocale = "en-US";

type LocaleValue = string | { [key: string]: LocaleValue };
type LocaleData = Record<string, LocaleValue>;
export class Locale {
  static setLabelsOnce(localeDataParm: LocaleData) {
    if (localeData !== localeDataParm) {
      localeData = localeDataParm;
      const resources: Record<string, { translation: LocaleValue }> = {};
      Object.entries(localeData).forEach(([key, value]) => {
        if (Object.prototype.hasOwnProperty.call(localeData, key)) {
          resources[key] = {
            translation: value // Use default namespace
          };
        }
      });
      if (!i18next.isInitialized) {
        i18next.init({
          lng: "en-US",
          debug: false,
          resources,
          interpolation: { prefix: "{", suffix: "}" }
        });
      } else {
        Object.entries(localeData).forEach(([locale, translations]) => {
          i18next.addResourceBundle(
            locale,
            "translation",
            translations,
            true,
            true
          );
        });
      }
      if (currentLocale !== "en-US") {
        Locale.change(currentLocale);
      }
    }
  }

  static exists(locale: string) {
    return locale in localeData;
  }

  static change(newLocale: string) {
    if (Locale.exists(newLocale)) {
      i18next.changeLanguage(newLocale);
      currentLocale = newLocale;
    }
    return currentLocale;
  }

  static getText(label: string, options = {}): string | React.ReactElement {
    const rval = i18next.t(label, options);
    if (rval) {
      const isBadActor =
        /<(basefont|input|iframe).*?>|<(applet|form|frameset|iframe|ruby|object|script).*?<\/\2>/i.test(
          rval
        );
      if (!isBadActor) {
        const isHTML =
          /<(br|basefont|hr|input|source|frame|param|area|meta|!--|col|link|option|base|img|wbr|!DOCTYPE).*?>|<(a|abbr|acronym|address|applet|article|aside|audio|b|bdi|bdo|big|blockquote|body|button|canvas|caption|center|cite|code|colgroup|command|datalist|dd|del|details|dfn|dialog|dir|div|dl|dt|em|embed|fieldset|figcaption|figure|font|footer|form|frameset|head|header|hgroup|h1|h2|h3|h4|h5|h6|html|i|iframe|ins|kbd|keygen|label|legend|li|map|mark|menu|meter|nav|noframes|noscript|object|ol|optgroup|output|p|pre|progress|q|rp|rt|ruby|s|samp|script|section|select|small|span|strike|strong|style|sub|sort-row|sup|table|tbody|td|textarea|tfoot|th|thead|time|title|tr|track|tt|u|ul|var|video).*?<\/\2>/i.test(
            rval
          );
        if (isHTML) {
          return Locale.sanitizedHtml(rval);
        }
      }
    }
    return rval;
  }

  static getCurrent() {
    return currentLocale;
  }

  static getLocale() {
    const urlLocale = Locale.getLocaleFromURL();
    const curLocale =
      urlLocale ||
      (navigator.languages && navigator.languages[0]) ||
      navigator.language;
    return (Locale.exists(curLocale) && curLocale) || "en-US";
  }

  static getLocaleFromURL(optionalUrl_ = "") {
    const optionalUrl = optionalUrl_ || window.location.search;
    const foundIdx = optionalUrl.indexOf("lang=");
    let locale = "";
    if (foundIdx > -1 && optionalUrl.length - foundIdx >= 10) {
      locale = optionalUrl.slice(foundIdx + 5, foundIdx + 10);
      locale = locale.replace("_", "-");
      if (!/([a-z][a-z]-[A-Z][A-Z])/.test(locale)) {
        locale = "";
      }
    }
    return locale;
  }

  static sanitizedHtml(text: string) {
    return (
      // eslint-disable-next-line react/no-danger
      <span
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(text, { USE_PROFILES: { html: true } })
        }}
      />
    );
  }
}
